'use client';

import { useRef, useTransition, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { isRedirectError } from 'next/dist/client/components/redirect-error';

import { setActiveProgramLensAction } from '@/lib/experience/program-lens-actions';

type ProgramLensActionButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onClick' | 'type'
> & {
  children: ReactNode;
  programFamily?: string;
  workspace?: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED';
  returnTo: string;
  pendingLabel?: string;
  onPendingChange?: (pending: boolean) => void;
  onSwitchError?: () => void;
};

/**
 * Canonical client trigger for the server-validated programme-lens action.
 * It keeps compact selectors out of nested/raw form markup while preserving
 * the action's authoritative re-read and redirect semantics.
 */
export function ProgramLensActionButton({
  children,
  disabled,
  programFamily,
  workspace,
  returnTo,
  pendingLabel,
  onPendingChange,
  onSwitchError,
  ...buttonProps
}: ProgramLensActionButtonProps) {
  const [isPending, startTransition] = useTransition();

  const inFlight = useRef(false);
  const selectProgram = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    onPendingChange?.(true);
    const formData = new FormData();
    formData.set('returnTo', returnTo);
    if (workspace) formData.set('workspace', workspace);
    else if (programFamily) formData.set('programFamily', programFamily);
    startTransition(async () => {
      try {
        await setActiveProgramLensAction(formData);
      } catch (error) {
        if (isRedirectError(error) || !onSwitchError) throw error;
        onSwitchError();
      } finally {
        inFlight.current = false;
        onPendingChange?.(false);
      }
    });
  };

  return (
    <button
      {...buttonProps}
      type="button"
      disabled={disabled || isPending}
      aria-busy={isPending || undefined}
      onClick={selectProgram}
    >
      {isPending && pendingLabel ? pendingLabel : children}
    </button>
  );
}
